import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../services/api';
import { ArrowLeft, Save, Loader2, Plus, Trash2 } from 'lucide-react';

interface Client {
  id: string;
  name: string;
  default_currency: string;
  default_payment_terms: number;
}

interface Project {
  id: string;
  name: string;
  budget_currency: string | null;
  status: string;
}

interface InvoiceItem {
  description: string;
  quantity: number;
  unit_price: string;
  tax_rate: string;
}

interface InvoiceData {
  client_id: string;
  project_id: string;
  invoice_date: string;
  due_date: string;
  currency: string;
  notes: string;
  terms: string;
  items: InvoiceItem[];
}

const emptyItem: InvoiceItem = {
  description: '',
  quantity: 1,
  unit_price: '0',
  tax_rate: '15',
};

export default function InvoiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [formData, setFormData] = useState<InvoiceData>({
    client_id: '',
    project_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'ZAR',
    notes: '',
    terms: 'Payment due within 30 days',
    items: [{ ...emptyItem }],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClients();
    fetchProjects();
    if (isEditing) {
      fetchInvoice();
    }
  }, [id]);

  const fetchClients = async () => {
    try {
      const data = await apiGet<{ items: Client[] }>('/api/clients?page_size=100');
      setClients(data.items);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await apiGet<{ items: Project[] }>('/api/projects?page_size=100&status=active');
      setProjects(data.items);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const fetchInvoice = async () => {
    setIsLoading(true);
    try {
      const data = await apiGet<any>(`/api/invoices/${id}`);
      setFormData({
        client_id: data.client_id,
        project_id: data.project_id || '',
        invoice_date: data.issue_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        due_date: data.due_date?.split('T')[0] || '',
        currency: data.currency || 'ZAR',
        notes: data.notes || '',
        terms: data.terms || '',
        items: data.items?.length ? data.items.map((item: any) => ({
          description: item.description || '',
          quantity: item.quantity || 1,
          unit_price: item.unit_price?.toString() || '0',
          tax_rate: item.tax_rate?.toString() || '15',
        })) : [{ ...emptyItem }],
      });
    } catch (err) {
      setError('Failed to load invoice');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClientChange = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      const paymentTerms = client.default_payment_terms || 30;
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + paymentTerms);
      setFormData(prev => ({
        ...prev,
        client_id: clientId,
        due_date: dueDate.toISOString().split('T')[0],
      }));
    } else {
      setFormData(prev => ({ ...prev, client_id: clientId }));
    }
  };

  const handleProjectChange = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      setFormData(prev => ({
        ...prev,
        project_id: projectId,
        currency: project.budget_currency || 'ZAR',
      }));
    } else {
      setFormData(prev => ({ ...prev, project_id: projectId }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.client_id) {
      setError('Please select a client');
      return;
    }
    if (!formData.project_id) {
      setError('Please select a project');
      return;
    }
    if (formData.items.length === 0 || !formData.items[0].description) {
      setError('Please add at least one line item');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        client_id: formData.client_id,
        project_id: formData.project_id,
        issue_date: formData.invoice_date,
        due_date: formData.due_date,
        currency: formData.currency,
        notes: formData.notes || null,
        terms: formData.terms || null,
        items: formData.items.filter(item => item.description).map(item => ({
          description: item.description,
          quantity: Number(item.quantity),
          unit_price: item.unit_price,
          tax_rate: item.tax_rate,
        })),
      };

      if (isEditing) {
        await apiPut(`/api/invoices/${id}`, payload);
      } else {
        await apiPost('/api/invoices', payload);
      }
      navigate('/admin/invoices');
    } catch (err: any) {
      setError(err.message || 'Failed to save invoice');
    } finally {
      setIsSaving(false);
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...emptyItem }],
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item),
    }));
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => {
      return sum + (Number(item.quantity) * Number(item.unit_price));
    }, 0);
  };

  const calculateTax = () => {
    return formData.items.reduce((sum, item) => {
      const lineTotal = Number(item.quantity) * Number(item.unit_price);
      return sum + (lineTotal * Number(item.tax_rate) / 100);
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate('/admin/invoices')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Invoices
        </button>
      </div>

      <div className="bg-dark-900 rounded-xl border border-dark-800 p-6">
        <h1 className="text-2xl font-bold text-white mb-6">
          {isEditing ? 'Edit Invoice' : 'New Invoice'}
        </h1>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client, Project & Currency */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Client *</label>
              <select
                value={formData.client_id}
                onChange={(e) => handleClientChange(e.target.value)}
                required
                className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Project *</label>
              <select
                value={formData.project_id}
                onChange={(e) => handleProjectChange(e.target.value)}
                required
                className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Currency</label>
              <input
                type="text"
                value={formData.currency}
                readOnly
                className="w-full px-4 py-2 bg-dark-700 border border-dark-700 rounded-lg text-gray-400 cursor-not-allowed"
              />
              {formData.project_id && (
                <p className="text-xs text-gray-500 mt-1">Currency is set by the project</p>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Invoice Date *</label>
              <input
                type="date"
                value={formData.invoice_date}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                required
                className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Due Date *</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                required
                className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Line Items */}
          <div>
            <h3 className="text-lg font-medium text-white mb-3">Line Items</h3>
            <div className="space-y-3">
              {formData.items.map((item, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-5">
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      min="0.01"
                      step="any"
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      step="0.01"
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      placeholder="Tax %"
                      value={item.tax_rate}
                      onChange={(e) => updateItem(index, 'tax_rate', e.target.value)}
                      step="0.01"
                      className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-2 text-red-400 hover:bg-dark-700 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addItem}
              className="mt-3 flex items-center gap-2 text-sm text-primary-500 hover:text-primary-400"
            >
              <Plus className="w-4 h-4" />
              Add Line Item
            </button>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-2 text-sm">
              <div className="flex justify-between text-gray-400">
                <span>Subtotal:</span>
                <span>{formData.currency} {calculateSubtotal().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Tax:</span>
                <span>{formData.currency} {calculateTax().toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white font-medium text-base pt-2 border-t border-dark-700">
                <span>Total:</span>
                <span>{formData.currency} {(calculateSubtotal() + calculateTax()).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes & Terms */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                placeholder="Notes visible to client"
                className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Terms</label>
              <textarea
                value={formData.terms}
                onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                rows={3}
                placeholder="Payment terms"
                className="w-full px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Save className="w-5 h-5" />
              )}
              {isSaving ? 'Saving...' : 'Save Invoice'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/invoices')}
              className="px-6 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
